/**
 * 申請回数 登録/編集 Modal.
 * AppModal + React Hook Form + Zod. Toast: useAppToast. Uniform input width.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { AppButton, AppForm, AppFormLabel, AppModal, AppSpin, AppTextField } from '@/components/atoms';
import { RA2NDatePicker } from '@/components/organisms/RA2NDatePicker';

import type { RezeptApplicationCountItem } from '@/services/rezeptApplicationCountService';
import {
  DEFAULT_FETCH_ERROR_MESSAGE,
  getApiErrorMessage,
  isNotFoundError,
} from '@/utils/apiError';
import useAppToast from '@/hooks/useAppToast';
import { useFormChangeGuard } from '@/hooks/useFormChangeGuard';
import { useRezeptApplicationCountOne } from '@/hooks/main/useRezeptApplicationCountEdit';
import {
  rezeptApplicationCountFormSchema,
  type RezeptApplicationCountFormInput
} from '@/schemas/rezeptApplicationCountSchema';
import {
  convGtJMonth,
  warekiMonthToYearmonth,
  yearmonthToDateStr,
} from '@/utils/dateConverter';

/** Payload sent to the API / used by onSave (rezept_yearmonth is a number in YYYYMM format). */
export interface RezeptApplicationCountFormData {
  rezept_yearmonth: number;
  rezept_application_count: number;
  rezept_related_application_count: number;
}

export interface RezeptApplicationCountEditModalProps {
  record: RezeptApplicationCountItem | null;
  patientId: string;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: RezeptApplicationCountFormData) => Promise<void>;
  onDelete?: (record: RezeptApplicationCountItem) => void;
  isNew: boolean;
}

/** Uniform width for all inputs in the form (uses the rezept-edit-modal-form CSS class). */
const INPUT_CLASS = 'rezept-edit-modal-input';

/** Block input when the value is a valid integer greater than 1000; let the schema validate other cases. */
const blockCountOver1000 =
  (onChange: (e: React.ChangeEvent<HTMLInputElement>) => void) =>
  (e: React.ChangeEvent<HTMLInputElement>) => {
    const trimmed = e.target.value.trim();
    if (trimmed !== '' && /^-?\d+$/.test(trimmed) && Number(trimmed) > 1000) {
      return;
    }
    onChange(e);
  };

const RezeptApplicationCountEditModal: React.FC<RezeptApplicationCountEditModalProps> = ({
  record,
  patientId,
  isOpen,
  onClose,
  onSave,
  onDelete,
  isNew,
}) => {
  const toast = useAppToast();
  const [initialFormData, setInitialFormData] = useState<RezeptApplicationCountFormInput>({
    rezept_yearmonth_str: '',
    rezept_application_count: '',
    rezept_related_application_count: '',
  });

  const isEditMode = isOpen && !isNew && record?.id != null;
  const recordIdStr = record?.id != null ? String(record.id) : undefined;
  const {
    data: detail,
    isLoading: detailLoading,
    isFetching: detailFetching,
    isError: isDetailError,
    error: detailError,
  } = useRezeptApplicationCountOne(
    isEditMode ? patientId : undefined,
    isEditMode ? recordIdStr : undefined,
  );

  const showDetailLoading = isEditMode && (detailLoading || detailFetching) && !isDetailError;

  /**
   * Set when onSubmit handles a 404 itself so the detail-fetch error effect
   * (which runs after cache invalidation triggers a refetch) doesn't toast again.
   */
  const submitHandled404Ref = useRef(false);
  const detailErrorHandledRef = useRef(false);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { isSubmitting },
  } = useTrimmedForm<RezeptApplicationCountFormInput>({
    resolver: zodResolver(rezeptApplicationCountFormSchema),
    mode: 'onBlur',
    defaultValues: {
      rezept_yearmonth_str: '',
      rezept_application_count: '',
      rezept_related_application_count: '',
    },
  });

  const formValues = watch();
  const { confirmIfChanged, clearFormChange } = useFormChangeGuard(formValues, initialFormData);

  useEffect(() => {
    const empty: RezeptApplicationCountFormInput = {
      rezept_yearmonth_str: '',
      rezept_application_count: '',
      rezept_related_application_count: '',
    };
    if (!isOpen) {
      reset(empty);
      setInitialFormData(empty);
      return;
    }
    if (isEditMode && !detail) return;
    const source = isEditMode ? detail : null;
    const next: RezeptApplicationCountFormInput = source
      ? {
          rezept_yearmonth_str: convGtJMonth(yearmonthToDateStr(source.rezept_yearmonth)),
          rezept_application_count: String(source.rezept_application_count ?? ''),
          rezept_related_application_count: String(source.rezept_related_application_count ?? ''),
        }
      : empty;
    reset(next);
    setInitialFormData(next);
  }, [isOpen, isEditMode, detail, reset]);

  useEffect(() => {
    if (!isOpen) {
      detailErrorHandledRef.current = false;
      return;
    }
    if (detailErrorHandledRef.current) return;
    if (!isEditMode || !isDetailError || !detailError) return;
    if (submitHandled404Ref.current) {
      submitHandled404Ref.current = false;
      return;
    }
    detailErrorHandledRef.current = true;
    const msg = isNotFoundError(detailError)
      ? 'データが見つかりません。'
      : getApiErrorMessage(detailError, DEFAULT_FETCH_ERROR_MESSAGE);
    toast.error(msg);
    clearFormChange();
    onClose();
  }, [isEditMode, isDetailError, detailError, clearFormChange, onClose, toast]);

  const handleClose = useCallback(() => {
    confirmIfChanged(() => {
      clearFormChange();
      onClose();
    });
  }, [confirmIfChanged, clearFormChange, onClose]);

  const onSubmit = async (data: RezeptApplicationCountFormInput) => {
    const rezept_yearmonth = warekiMonthToYearmonth(data.rezept_yearmonth_str);
    if (!rezept_yearmonth) {
      toast.error('施術年月を入力してください。');
      return;
    }
    try {
      await onSave({
        rezept_yearmonth,
        rezept_application_count: data.rezept_application_count.trim() === '' ? 0 : Number(data.rezept_application_count),
        rezept_related_application_count: data.rezept_related_application_count.trim() === '' ? 0 : Number(data.rezept_related_application_count),
      });
      toast.success('更新が正常に終了しました。');
      clearFormChange();
      onClose();
    } catch (err) {
      if (isNotFoundError(err)) {
        // Record was deleted in another tab/session. Toast once and close;
        // the cache-triggered detail refetch will also 404 — skip its toast.
        submitHandled404Ref.current = true;
        toast.error('データが見つかりません。');
        clearFormChange();
        onClose();
        return;
      }
      const apiErrors = (err as { response?: { status?: number; data?: { errors?: Record<string, string[]> } } })
        ?.response?.data?.errors;
      if (apiErrors && typeof apiErrors === 'object') {
        Object.entries(apiErrors).forEach(([key, messages]) => {
          const message = Array.isArray(messages) ? messages[0] : String(messages ?? '');
          if (!message) return;
          const targetField =
            key === 'rezept_yearmonth' ||
            key === 'rezept_yearmonth_str' ||
            key === 'treatment_yearmonth'
              ? 'rezept_yearmonth_str'
              : key;
          setError(targetField as keyof RezeptApplicationCountFormInput, {
            type: 'manual',
            message,
          });
        });
      }
      toast.error('更新ができませんでした。');
    }
  };

  return (
    <AppModal
      isOpen={isOpen}
      onClose={handleClose}
      maxWidth="max-w-lg"
      title={isNew ? '申請回数登録' : '申請回数編集'}
      footer={null}
    >
      <form
        onSubmit={handleSubmit(onSubmit, () => {
          toast.error('更新ができませんでした。');
        })}
        className="rezept-edit-modal-form relative"
      >
        {showDetailLoading && (
          <div
            className="absolute inset-0 z-20 flex items-center justify-center rounded-md bg-white/70 backdrop-blur-sm"
            aria-busy="true"
            aria-label="読み込み中"
          >
            <AppSpin size="large" />
          </div>
        )}
        <div className="grid grid-cols-[180px_1fr] gap-x-4 gap-y-4 items-start">
          <AppFormLabel label="施術年月" required />
          <Controller
            name="rezept_yearmonth_str"
            control={control}
            render={({ field, fieldState }) => (
              <AppForm.Item
                className="mb-0"
                validateStatus={fieldState.invalid ? 'error' : undefined}
                help={fieldState.error?.message}
              >
                <RA2NDatePicker
                  picker="month"
                  value={field.value || ''}
                  onChange={val => field.onChange(val ?? '')}
                  className={INPUT_CLASS}
                  placeholder="年月指定"
                  error={fieldState.invalid}
                />
              </AppForm.Item>
            )}
          />

          <AppFormLabel label="医療費最終申請回数" />
          <Controller
            name="rezept_application_count"
            control={control}
            render={({ field, fieldState }) => (
              <AppForm.Item
                className="mb-0"
                validateStatus={fieldState.invalid ? 'error' : undefined}
                help={fieldState.error?.message}
              >
                <AppTextField
                  {...field}
                  onChange={blockCountOver1000(field.onChange)}
                  className={INPUT_CLASS}
                  status={fieldState.invalid ? 'error' : undefined}
                />
              </AppForm.Item>
            )}
          />

          <AppFormLabel label="助成費最終申請回数" />
          <Controller
            name="rezept_related_application_count"
            control={control}
            render={({ field, fieldState }) => (
              <AppForm.Item
                className="mb-0"
                validateStatus={fieldState.invalid ? 'error' : undefined}
                help={fieldState.error?.message}
              >
                <AppTextField
                  {...field}
                  onChange={blockCountOver1000(field.onChange)}
                  className={INPUT_CLASS}
                  status={fieldState.invalid ? 'error' : undefined}
                />
              </AppForm.Item>
            )}
          />
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <AppButton
            danger
            type="default"
            onClick={() => record && onDelete?.(record)}
            disabled={isSubmitting || isNew || !record || !onDelete}
          >
            削除
          </AppButton>
          <AppButton type="primary" htmlType="submit" disabled={isSubmitting}>
            {isSubmitting ? '保存中...' : '更新'}
          </AppButton>
        </div>
      </form>
    </AppModal>
  );
};

export default RezeptApplicationCountEditModal;
